import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ticketsApi } from '../../api/tickets';
import { categoriesApi } from '../../api/categories';
import { useClassifications } from '../../hooks/useClassifications';
import { fieldHelp } from '../../lib/fieldHelp';
import { HelpCircle } from 'lucide-react';
import FileUploader from '../../components/FileUploader';
import { insertUploadingPlaceholder, resolveUploadingPlaceholder, hasPendingInlineUploads } from '../../lib/inlineImages';
import type { UploadedAttachment } from '../../api/attachments';
import PageHeader from '../../components/PageHeader';
import toast from 'react-hot-toast';

export default function PortalTicketCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    description: '',
    categoryId: '',
    priority: 'normal' as string,
  });

  // The company's priority list — a customer picking from the portal sees the
  // same values staff do, including any the company added or renamed.
  const { optionsFor } = useClassifications();
  const priorityOptions = optionsFor('priority', form.priority);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  /** For inserting inline-image references at the caret on paste. */
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      ticketsApi.create({
        title: form.title,
        description: form.description,
        categoryId: form.categoryId || undefined,
        priority: form.priority as any,
        source: 'portal',
        ...(attachments.length > 0 && {
          attachments: attachments.map(a => ({ filename: a.filename, url: a.url, size: a.size, mimeType: a.mimeType })) as any,
        }),
      }),
    onSuccess: (ticket) => {
      toast.success('Ticket created successfully');
      navigate(`/portal/tickets/${ticket.id ?? (ticket as any)._id}`);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create ticket'),
  });

  // Screenshot pasted while typing in the description: put the image inline at
  // the caret (placeholder now, real ![name](url) ref when the upload lands).
  // Pastes landing anywhere else keep the old behavior — attachment chip only.
  const handlePasteImageStart = (id: string, file: File) => {
    const el = descriptionRef.current;
    if (!el || document.activeElement !== el) return;
    const description = insertUploadingPlaceholder(el, form.description, id, file.name);
    setForm(f => ({ ...f, description }));
  };

  const handlePasteImageDone = (id: string, att: UploadedAttachment | null) => {
    const description = resolveUploadingPlaceholder(descriptionRef.current, form.description, id, att);
    setForm(f => ({ ...f, description }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    if (hasPendingInlineUploads(form.description)) {
      toast.error('A pasted screenshot is still uploading — one moment');
      return;
    }
    createMutation.mutate();
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Create a Ticket"
        intro="Describe your issue and we'll get back to you as soon as possible."
      />

      <form onSubmit={handleSubmit} className="card space-y-5">
        <div>
          <label className="label" title={fieldHelp('Subject')}>
            Subject
            <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Brief description of your issue"
            className="input"
            required
          />
        </div>

        <div>
          <label className="label" title={fieldHelp('Description')}>
            Description
            <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
          </label>
          <textarea
            ref={descriptionRef}
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Please provide details about your issue, including any steps to reproduce... (paste a screenshot to add it inline)"
            rows={6}
            className="input resize-y"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label" title={fieldHelp('Category')}>
            Category
            <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
          </label>
            <select
              value={form.categoryId}
              onChange={(e) => setForm(f => ({ ...f, categoryId: e.target.value }))}
              className="select"
            >
              <option value="">Select category</option>
              {(categories ?? []).map((c: any) => (
                <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" title={fieldHelp('Priority')}>
            Priority
            <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
          </label>
            <select
              value={form.priority}
              onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
              className="select"
            >
              {priorityOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label" title={fieldHelp('Attachments')}>
            Attachments
            <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
          </label>
          <p className="text-xs text-muted mb-1.5">Add screenshots or files — you can also paste an image directly.</p>
          <FileUploader
            attachments={attachments}
            onChange={setAttachments}
            enablePaste
            context="ticket"
            onPasteImageStart={handlePasteImageStart}
            onPasteImageDone={handlePasteImageDone}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate('/portal/tickets')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary disabled:opacity-50">
            {createMutation.isPending ? 'Creating...' : 'Submit Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
